import type { KioskOperator } from './hooks';

const BODY_FONT = "-apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif";

function initialsColor(name: string): string {
  const colors = ['#6CB4EE', '#12B76A', '#F59E0B', '#8B5CF6', '#EC4899', '#14B8A6', '#F97316'];
  let h = 0;
  for (let i = 0; i < name.length; i++) h = name.charCodeAt(i) + ((h << 5) - h);
  return colors[Math.abs(h) % colors.length];
}

function initials(name: string): string {
  return name.split(' ').map(w => w[0]).filter(Boolean).join('').toUpperCase().slice(0, 2);
}

interface Props {
  operators: KioskOperator[];
  selectionMode: 'optional' | 'required';
  accentColor: string;
  headingFont: string;
  onSelect: (operator: KioskOperator | null) => void;
}

export function KioskOperatorPicker({ operators, selectionMode, accentColor, headingFont, onSelect }: Props) {
  return (
    <div style={{ padding: '0 24px' }}>
      <h2 style={{ fontSize: 20, fontWeight: 600, color: '#1A1A1A', fontFamily: headingFont, margin: '28px 0 16px' }}>
        Who would you like to see?
      </h2>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        {selectionMode === 'optional' && (
          <button onClick={() => onSelect(null)} style={{
            background: '#fff', border: `2px dashed ${accentColor}60`, borderRadius: 14,
            padding: '16px 18px', cursor: 'pointer', fontFamily: BODY_FONT,
            display: 'flex', alignItems: 'center', gap: 14, textAlign: 'left',
            transition: 'border-color 200ms ease, background 200ms ease',
          }}
            onMouseEnter={e => { e.currentTarget.style.borderColor = accentColor; e.currentTarget.style.background = `${accentColor}08`; }}
            onMouseLeave={e => { e.currentTarget.style.borderColor = `${accentColor}60`; e.currentTarget.style.background = '#fff'; }}
          >
            <div style={{
              width: 48, height: 48, borderRadius: 24, background: `${accentColor}14`,
              display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 20, color: accentColor,
            }}>✓</div>
            <div>
              <div style={{ fontSize: 15, fontWeight: 500, color: '#1A1A1A' }}>Anyone available</div>
              <div style={{ fontSize: 13, color: '#636366', marginTop: 2 }}>First open spot</div>
            </div>
          </button>
        )}
        {operators.map(op => (
          <button key={op.id} onClick={() => onSelect(op)} style={{
            background: '#fff', border: '1px solid #E5E5EA', borderRadius: 14,
            padding: '16px 18px', cursor: 'pointer', fontFamily: BODY_FONT,
            display: 'flex', alignItems: 'center', gap: 14, textAlign: 'left',
            transition: 'border-color 200ms ease, box-shadow 200ms ease',
          }}
            onMouseEnter={e => { e.currentTarget.style.borderColor = accentColor; e.currentTarget.style.boxShadow = `0 0 0 1px ${accentColor}`; }}
            onMouseLeave={e => { e.currentTarget.style.borderColor = '#E5E5EA'; e.currentTarget.style.boxShadow = 'none'; }}
          >
            {op.avatar_url ? (
              <img src={op.avatar_url} alt="" style={{ width: 48, height: 48, borderRadius: 24, objectFit: 'cover' }} />
            ) : (
              <div style={{
                width: 48, height: 48, borderRadius: 24, background: initialsColor(op.name),
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                color: '#fff', fontSize: 17, fontWeight: 600, fontFamily: BODY_FONT,
              }}>{initials(op.name)}</div>
            )}
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 15, fontWeight: 500, color: '#1A1A1A' }}>{op.name}</div>
              {op.bio && <div style={{ fontSize: 13, color: '#636366', marginTop: 2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{op.bio}</div>}
            </div>
          </button>
        ))}
      </div>
    </div>
  );
}
